/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";
import { useState, useEffect } from "react";
import api from "utils/api";

// Default image for notifications
import defaultImage from "assets/images/team-2.jpg";

// Format date helper
function formatDate(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Truncate text helper
function truncateText(text, maxLength = 50) {
  if (!text) return "N/A";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

const NotificationTableData = (handleEditClick, handleDeleteClick, searchQuery = "") => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      console.log("🔄 Fetching notifications from API...");
      console.log("📍 API Base URL:", api.defaults.baseURL);
      console.log("📍 Making request to: /notification");

      const response = await api.get("/notification");
      console.log("✅ Raw API response:", response);
      console.log("📦 Response data:", response.data);
      console.log("🎯 Response status:", response.status);

      if (response.data && response.data.success) {
        console.log("✅ Success! Notifications count:", response.data.data?.length || 0);
        console.log("📋 Notifications data:", response.data.data);
        setNotifications(response.data.data || []);
      } else {
        console.error("❌ API returned unsuccessful response:", response.data);
        setNotifications([]);
      }
    } catch (error) {
      console.error("💥 Error fetching notifications:", error);
      console.error("🔍 Error message:", error.message);
      console.error("🔍 Error response:", error.response);
      console.error("🔍 Error response data:", error.response?.data);
      console.error("🔍 Error response status:", error.response?.status);
      console.error("🔍 Error config:", error.config);
      setNotifications([]);
    } finally {
      setLoading(false);
      console.log("✅ Fetch notifications completed");
    }
  };

  const columns = [
    { name: "sno", align: "center" },
    { name: "Title", align: "left" },
    { name: "Image", align: "left" },
    { name: "Description", align: "left" },
    { name: "Type", align: "center" },
    { name: "User Type", align: "center" },
    { name: "Platform", align: "center" },
    { name: "Created On", align: "center" },
    // { name: "Actions", align: "center" },
  ];

  // Filter notifications based on search query
  const filteredNotifications = notifications.filter((notification) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const title = (notification.title || "").toLowerCase();
    const description = (notification.description || "").toLowerCase();
    const type = (notification.type || "").toLowerCase();
    const userType = (notification.userType || "").toLowerCase();
    const platform = (notification.notificationPlatform || "").toLowerCase();

    return (
      title.includes(query) ||
      description.includes(query) ||
      type.includes(query) ||
      userType.includes(query) ||
      platform.includes(query)
    );
  });

  const rows = loading
    ? [
        {
          sno: (
            <ArgonTypography variant="caption" color="text">
              Loading...
            </ArgonTypography>
          ),
          Title: (
            <ArgonTypography variant="caption" color="text">
              Loading...
            </ArgonTypography>
          ),
          Image: <ArgonAvatar alt="Loading" size="sm" variant="rounded" />,
          Description: (
            <ArgonTypography variant="caption" color="text">
              Loading...
            </ArgonTypography>
          ),
          Type: (
            <ArgonTypography variant="caption" color="text">
              -
            </ArgonTypography>
          ),
          "User Type": (
            <ArgonTypography variant="caption" color="text">
              -
            </ArgonTypography>
          ),
          Platform: (
            <ArgonTypography variant="caption" color="text">
              -
            </ArgonTypography>
          ),
          "Created On": (
            <ArgonTypography variant="caption" color="text">
              -
            </ArgonTypography>
          ),
          // Actions: (
          //   <ArgonTypography variant="caption" color="text">
          //     -
          //   </ArgonTypography>
          // ),
        },
      ]
    : filteredNotifications.map((notification, index) => ({
        sno: (
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            {index + 1}
          </ArgonTypography>
        ),
        Title: (
          <Tooltip title={notification.title || "No title"}>
            <ArgonTypography variant="caption" fontWeight="medium" color="text">
              {truncateText(notification.title, 30)}
            </ArgonTypography>
          </Tooltip>
        ),
        Image: (
          <ArgonAvatar
            src={notification.photo}
            alt={notification.title || "Notification"}
            size="sm"
            variant="rounded"
          />
        ),
        Description: (
          <Tooltip title={notification.description || "No description"}>
            <ArgonTypography variant="caption" color="text">
              {truncateText(notification.description, 40)}
            </ArgonTypography>
          </Tooltip>
        ),
        Type: (
          <ArgonBadge
            badgeContent={notification.type || "N/A"}
            color={notification.type === "NOTICE" ? "warning" : "primary"}
            size="xs"
          />
        ),
        "User Type": (
          <ArgonTypography variant="caption" color="text">
            {notification.userType?.replace(/_/g, " ") || "N/A"}
          </ArgonTypography>
        ),
        Platform: (
          <ArgonBadge
            badgeContent={notification.notificationPlatform || "NORMAL"}
            color={
              notification.notificationPlatform === "WHATSAPP"
                ? "success"
                : notification.notificationPlatform === "PUSH"
                ? "info"
                : "secondary"
            }
            size="xs"
          />
        ),
        "Created On": (
          <ArgonTypography variant="caption" color="text">
            {formatDate(notification.createdAt)}
          </ArgonTypography>
        ),
        // Actions: (
        //   <ArgonBox display="flex" gap={1}>
        //     <Tooltip title="Edit Notification">
        //       <IconButton
        //         size="small"
        //         onClick={() => handleEditClick && handleEditClick(notification)}
        //         sx={{ color: "#1976d2" }}
        //       >
        //         <EditIcon fontSize="small" />
        //       </IconButton>
        //     </Tooltip>
        //     <Tooltip title="Delete Notification">
        //       <IconButton
        //         size="small"
        //         onClick={() => handleDeleteClick && handleDeleteClick(notification)}
        //         sx={{ color: "#d32f2f" }}
        //       >
        //         <DeleteIcon fontSize="small" />
        //       </IconButton>
        //     </Tooltip>
        //   </ArgonBox>
        // ),
      }));

  return {
    columns,
    rows,
    loading,
    notifications,
    refetch: fetchNotifications,
  };
};

export default NotificationTableData;
